import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { TripGroupingInput } from '../../../lib/types/models/trip-grouping/trip-grouping-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { yearsFilterOptions } from '../../../views/pcr-personal-space/utils'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<TripGroupingInput, true> & {
  className?: string
  variant?: 'pure' | 'regular'
}

export const tripGroupingFormSchema: ZodType<NullableTransformer<TripGroupingInput>> = z.object({
  name: createStringSchema('NOM'),
  year: createNumberSchema('ANNÉE'),
  tripIds: createNumberArraySchema('voyages').nullish()
})

export const TripGroupingForm = ({
  className,
  defaultValues,
  variant = 'regular',
  ...validationButtonProps
}: Props) => {
  const form = useForm<TripGroupingInput>({
    resolver: zodResolver(tripGroupingFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { year } = form.watch()

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox
          className={cn('p-4 rounded-2xl', {
            'p-0 mb-4 border-0': variant === 'pure'
          })}
        >
          <div className={cn('flex flex-col gap-3', className)}>
            {variant === 'regular' && (
              <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
                Groupe de voyages
              </Typography>
            )}

            <div className={'flex gap-4 flex-row'}>
              <FormField
                control={form.control}
                name={'name'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput required {...field} type={'text'} label={'NOM du groupe'} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'year'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        required
                        {...field}
                        label={'ANNÉE du groupe'}
                        options={yearsFilterOptions}
                        selectedOption={year ? { label: year.toString(), value: year } : undefined}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </InnerBox>

        <ValidationButtons hasBorderTop={variant === 'pure'} {...validationButtonProps} />
      </form>
    </Form>
  )
}
